import Stripe from 'stripe'
import { PaymentPlanType } from './payment-plan.type'

export type StripePaymentMethod =
  | Stripe.PaymentMethod
  | { id: string; type: Stripe.PaymentMethod.Type }
  | null
export interface SubscriptionRequest {
  id: string
  stripe_subscription_id: string
  stripe_product_id: string
  stripe_schedule_id?: string
  stripe_customer_id: string
}

export type PlanV1 = {
  id: number
  name: string
  stripe_product_id: string
  trial_days: number
  monthly_price: number
  annual_price: number
  annual_saving: number
  features: string
  main_feature: string
  nickname?: string
}

export interface GetSubscriptionResponseV1 {
  request: any | null
  stripe_subscription: (Stripe.Subscription & { currentTimeOverride?: number }) | null
  stripe_customer: Stripe.Customer | Stripe.DeletedCustomer | undefined
  plan: PlanV1 | undefined
  stripe_schedule?: Stripe.SubscriptionSchedule | null
  stripe_customer_schedules?: Stripe.Response<Stripe.ApiList<Stripe.SubscriptionSchedule>>
  stripe_payment_method?: StripePaymentMethod
  can_trial: boolean
  /**
   * The payment intent status for the current invoice
   *  "canceled" | "processing" | "requires_action" | "requires_capture" | "requires_confirmation" | "requires_payment_method" | "succeeded"
   */
  latest_invoice_payment_status?: Stripe.PaymentIntent.Status | null
  subv2_data?: SubscriptionV2Data | null
}

/**
 * overall sub level of the user
 *
 * trial      = new user trialing pro; will be downgraded at trial_end_at
 * free       = trial expired and user did not elect to pay subscription
 * essentials = currently on essentials plan (may or may not be canceling)
 * pro        = currently on pro plan (may or may not be canceling)
 *
 * For essentials and pro, user may have canceled.
 * If cancel_at_period_end is true, user has canceled and their
 * sub will expire at current_period_end
 *
 * note:  when on free account, they never "expire" so you can ignore
 *        current_period_end and trial_end_at
 */
export enum SubscriptionV2State {
  Trial = 'Trial',
  Free = 'Free',
  Essentials = 'Essentials',
  Pro = 'Pro'
}

/**
 * not sure how much of this will end up getting used
 */
export type SubscriptionV2Data = {
  state: SubscriptionV2State
  /**
   * milliseconds start timestamp for trial
   */
  trial_start_at: number | null
  /**
   * milliseconds expiration timestamp for trial
   */
  trial_end_at: number | null
  /**
   * end date of the current subscription cycle (milliseconds)
   * populated for any subscription that has a current_period_end, including trials
   */
  current_period_end: number | null
  stripe_customer_id: string
  locations: number
  latest_payment_intent_status: Stripe.PaymentIntent.Status | null
  payment_method: Stripe.PaymentMethod | { id: string; type: Stripe.PaymentMethod.Type } | null
}

export type GetSubscriptionResponseV2 = GetSubscriptionResponseV1 & {
  /**
   * will always be null because this is from the old method
   */
  request: null
  /**
   * should always be defined; everyone is subscribed to something in V2
   */
  stripe_subscription: Stripe.Subscription & { currentTimeOverride?: number }
  /**
   * should always be defined; there's always a customer in V2
   */
  stripe_customer: Stripe.Customer | Stripe.DeletedCustomer
  /**
   * should always be defined; there's always a plan V2
   */
  plan: Stripe.Plan
  /**
   * Stripe schedule is returned so we can show correct messaging to user about their scheduled changes
   */
  stripe_schedule: Stripe.SubscriptionSchedule | null
  /**
   * always false with V2 - user can never start a new trial
   * after the initial one that's automatic
   */
  can_trial: false
  /**
   * only defined when the subscription is a "V2" subscription
   */
  subv2_data?: SubscriptionV2Data
}

export type GetSubscriptionResponse = GetSubscriptionResponseV1 | GetSubscriptionResponseV2

type StripeProductMetadata = {
  featureHeadline: string
}

export interface GetProducts extends Stripe.Product {
  plans: StripePlan[]
}
export type GetPlansResponse = (GetProducts & PaymentPlanType)[]
export type GetPlansV2Response = Product[]

export type StripePlan = Stripe.Plan & {
  marketing_features?: { name: string }[]
  metadata?: StripeProductMetadata

  baseAmount: number
  discountedAmount?: number
  isDiscounted: boolean

  discount?: number
  discountName?: string
  discountDuration?: 'once' | 'monthly'

  isUpgrade: boolean
  isDowngrade: boolean
  isToFree: boolean
  appliesAtRenewal: boolean

  totalDueNow?: number
  prorationAmount?: number
}

export type Product = {
  active: boolean
  annual_price: number // in dollars  // from the blindtiger.payment_plans model,
  annual_saving: number // in dollars  // from the blindtiger.payment_plans model,
  attributes: string[]
  created: number
  default_price: number | null
  description: string
  features: string[]
  id: PlansEnum
  images: string[]
  livemode: boolean // if the product is a live or test product,
  main_feature: string // from the blindtiger.payment_plans model
  marketing_features: Record<'name', string> // from the Stripe product setup,
  metadata?: StripeProductMetadata // from stripe,
  monthly_price: number
  name: string
  object: string
  package_dimensions: string | null
  plans: StripePlan[]
  shippable: boolean | null
  statement_descriptor: string
  stripe_product_id: PlansEnum | string
  tax_code: string | null
  trial_days: number // from blindtiger.payment_plans model,
  type: string
  unit_label: string
  updated: number
  url: string | null
}

export enum PlansEnum {
  Basic = 0,
  Essential = 1,
  Professional = 2
}

export enum PlanInterval {
  Month = 'month',
  Year = 'year'
}

export type GetPlansQuery = {
  customerId?: string
  subscriptionId?: string
  venueCount?: number
}
